import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowDown, ArrowRight } from "./ProjectIcons";
import Spinner from "./Spinner";

/** `null` = a response with no pass/fail meaning — neutral, not a false green. */
export function testResponseClasses(isSuccess: boolean | null): string {
    if (isSuccess === null) return "bg-light-gray text-dark-gray";
    return isSuccess
        ? "bg-light-green text-dark-green"
        : "bg-very-light-orange text-dark-gray";
}

export function testResponsePreClasses(isSuccess: boolean | null): string {
    return `${testResponseClasses(isSuccess)} p-2 rounded-sm text-xs w-full overflow-auto max-h-60`;
}

export type TestActionVerdict = {
    message: string;
    /** true → green, false → orange, null → neutral. */
    isSuccess: boolean | null;
    /** Omit and the "View Details" toggle isn't rendered. */
    details?: React.ReactNode;
};

type TestActionLayoutProps = {
    formTitle?: React.ReactNode;
    /** The trigger. Each flow owns its own gating, label and tooltips. */
    button: React.ReactNode;
    isLoading: boolean;
    verdict: TestActionVerdict | null;
    /** Derives `${testIdPrefix}-result`, `-details-toggle`, `-details`. */
    testIdPrefix?: string;
};

/** Shared chrome for "run a test action and show what came back". Both the
 *  saved-integration flow (TestAction) and the draft one
 *  (DraftTestActionButton) render through this so they can't drift apart —
 *  only the trigger and the details body differ between them. */
export const TestActionLayout: React.FC<TestActionLayoutProps> = ({
    formTitle,
    button,
    isLoading,
    verdict,
    testIdPrefix,
}) => {
    const { t } = useTranslation();
    const [isDetailVisible, setDetailVisible] = useState(true);

    const showVerdict = !isLoading && !!verdict;

    return (
        <div className="flex flex-col w-full">
            <div className="flex flex-row items-end justify-between w-full">
                <div className="text-2xl font-normal text-secondary-gray transition-all duration-300">{formTitle}</div>
                <div className="flex flex-row items-center">
                    {button}
                    {isLoading && <div className="ml-3"><Spinner /></div>}
                    {/* Always mounted: a live region has to exist before its
                        content changes to be announced. */}
                    <div
                        role="status"
                        aria-live="polite"
                        className={showVerdict ? "ml-3" : "sr-only"}
                        data-testid={testIdPrefix ? `${testIdPrefix}-result` : undefined}
                    >
                        {showVerdict && (
                            <p className={`${testResponseClasses(verdict.isSuccess)} p-2 rounded-sm`}>
                                {verdict.message}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {showVerdict && verdict.details && (
                <div className="flex flex-col items-start w-full">
                    <button
                        type="button"
                        className="my-2 text-dark-gray hover:text-dark-green flex items-center pl-0 ml-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark-green focus-visible:ring-offset-2"
                        onClick={() => setDetailVisible(!isDetailVisible)}
                        aria-expanded={isDetailVisible}
                        data-testid={testIdPrefix ? `${testIdPrefix}-details-toggle` : undefined}
                    >
                        {isDetailVisible ? <ArrowDown /> : <ArrowRight />}
                        {t("View Details")}
                    </button>
                    {isDetailVisible && (
                        <div className="w-full" data-testid={testIdPrefix ? `${testIdPrefix}-details` : undefined}>
                            {verdict.details}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TestActionLayout;
